import React, { useEffect, useRef, useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import { GlobalContext } from '../../providers/GlobalProvider';
import { ExportHubAPI, OverviewProduct } from 'api/ExportHubAPI';
import { TierBadge } from './productPresets';

/**
 * Global product search (spec §7.7) in the Level-1 header. Searches products
 * across all of a team's databases (reusing the overview endpoint) and lands on
 * the selected product's 360 / history view.
 */
const GlobalProductSearch: React.FC = () => {
  const { cur_user } = useContext(GlobalContext);
  const history = useHistory();
  const groupId = cur_user?.selected_team?.id;

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<OverviewProduct[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    const q = query.trim();
    if (!q || !groupId) {
      setResults([]);
      return undefined;
    }
    setLoading(true);
    const t = setTimeout(() => {
      ExportHubAPI.getOverviewProducts(groupId, { search: q, limit: 8 })
        .then((res) => {
          setResults(res.products);
          setOpen(true);
        })
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [query, groupId]);

  const goTo = (p: OverviewProduct): void => {
    setOpen(false);
    setQuery('');
    history.push(`/export-hub/products/${p.id}/history`);
  };

  return (
    <div className='export-hub-search eh-global-search' ref={ref}>
      <SearchIcon className='export-hub-search-icon' style={{ fontSize: 18 }} />
      <input
        type='text'
        className='export-hub-search-input'
        placeholder='Search products by name, SKU, or HTS'
        aria-label='Search products'
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && results[0]) goTo(results[0]);
          if (e.key === 'Escape') setOpen(false);
        }}
      />
      {open && (
        <div className='eh-search-menu'>
          {loading && results.length === 0 && (
            <div className='eh-search-empty'>Searching…</div>
          )}
          {!loading && results.length === 0 && (
            <div className='eh-search-empty'>No matching products.</div>
          )}
          {results.map((p) => (
            <button
              key={p.id}
              type='button'
              className='eh-search-row'
              onClick={() => goTo(p)}
            >
              <span className='eh-search-main'>
                <span className='eh-search-name'>
                  {p.name}
                  <TierBadge value={p.tier} />
                </span>
                <span className='eh-search-sub'>
                  {p.internal_sku ? `SKU ${p.internal_sku}` : 'No SKU'}
                  {p.hts_code ? ` · ${p.hts_code}` : ''}
                  {p.database_name ? ` · ${p.database_name}` : ''}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default GlobalProductSearch;
